// Pronouns — subject, object (direct/indirect), reflexive, prepositional, and
// the placement rules that trip learners up. A consolidated pronoun chart sits
// at the bottom as a quick reference.
import { renderGuideSections } from "../ui/guideSections.js";

export function pronounsView(app) {
  const chart = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Pronoun Reference Chart</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Person</th>
          <th class="py-2 px-3">Subject</th>
          <th class="py-2 px-3">Direct obj.</th>
          <th class="py-2 px-3">Indirect obj.</th>
          <th class="py-2 px-3">Reflexive</th>
          <th class="py-2 px-3">After prep.</th>
        </tr></thead>
        <tbody>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">I</td><td class="py-2 px-3">yo</td><td class="py-2 px-3">me</td><td class="py-2 px-3">me</td><td class="py-2 px-3">me</td><td class="py-2 px-3">mí</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (inf.)</td><td class="py-2 px-3">tú</td><td class="py-2 px-3">te</td><td class="py-2 px-3">te</td><td class="py-2 px-3">te</td><td class="py-2 px-3">ti</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">he / it</td><td class="py-2 px-3">él</td><td class="py-2 px-3">lo</td><td class="py-2 px-3">le</td><td class="py-2 px-3">se</td><td class="py-2 px-3">él</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">she / it</td><td class="py-2 px-3">ella</td><td class="py-2 px-3">la</td><td class="py-2 px-3">le</td><td class="py-2 px-3">se</td><td class="py-2 px-3">ella</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (formal)</td><td class="py-2 px-3">usted</td><td class="py-2 px-3">lo/la</td><td class="py-2 px-3">le</td><td class="py-2 px-3">se</td><td class="py-2 px-3">usted</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">we</td><td class="py-2 px-3">nosotros/as</td><td class="py-2 px-3">nos</td><td class="py-2 px-3">nos</td><td class="py-2 px-3">nos</td><td class="py-2 px-3">nosotros/as</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you all (inf., Spain)</td><td class="py-2 px-3">vosotros/as</td><td class="py-2 px-3">os</td><td class="py-2 px-3">os</td><td class="py-2 px-3">os</td><td class="py-2 px-3">vosotros/as</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">they (m.)</td><td class="py-2 px-3">ellos</td><td class="py-2 px-3">los</td><td class="py-2 px-3">les</td><td class="py-2 px-3">se</td><td class="py-2 px-3">ellos</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">they (f.)</td><td class="py-2 px-3">ellas</td><td class="py-2 px-3">las</td><td class="py-2 px-3">les</td><td class="py-2 px-3">se</td><td class="py-2 px-3">ellas</td></tr>
          <tr><td class="py-2 pr-3 font-medium">you all (formal)</td><td class="py-2 px-3">ustedes</td><td class="py-2 px-3">los/las</td><td class="py-2 px-3">les</td><td class="py-2 px-3">se</td><td class="py-2 px-3">ustedes</td></tr>
        </tbody>
      </table></div>
      <p class="text-sage-500 text-xs mt-3">Special prepositional forms: <i>conmigo</i> (with me), <i>contigo</i> (with you) — never <i>con mí / con ti</i>.</p>
    </div>
  `;

  renderGuideSections(
    app,
    "Pronouns",
    "Pronouns stand in for nouns so you don't repeat them. Spanish has a different pronoun for each grammatical role — subject, direct object, indirect object, reflexive, and the form used after a preposition.",
    [
      {
        title: "1. Subject pronouns — often dropped",
        body: `
          <p class="text-sage-700 mb-3">The verb ending already shows who the subject is, so subject pronouns are usually <b>omitted</b>: <i>Hablo español</i> (not <i>Yo hablo español</i>) unless you need them for emphasis or contrast.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Emphasis:</b> <i><b>Yo</b> pago</i> — <i>I'll</i> pay (not you).</li>
            <li><b>Contrast:</b> <i>Él trabaja pero ella estudia.</i></li>
            <li><b>tú vs usted:</b> <i>tú</i> is informal, <i>usted</i> is formal/respectful (and takes <i>he/she</i> verb forms).</li>
          </ul>
        `,
      },
      {
        title: "2. Direct vs indirect object pronouns",
        body: `
          <p class="text-sage-700 mb-2">The <b>direct object</b> receives the action (<i>what?</i>); the <b>indirect object</b> is who benefits (<i>to/for whom?</i>).</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-2">
            <li><b>Direct:</b> <i>Veo <u>el libro</u> → Lo veo.</i> (I see it.)</li>
            <li><b>Indirect:</b> <i>Doy el libro <u>a María</u> → Le doy el libro.</i> (I give her the book.)</li>
          </ul>
          <p class="text-sage-700 text-sm">When both appear, indirect comes first, and <b>le/les become se</b> before lo/la/los/las: <i>Le doy el libro → <b>Se</b> lo doy.</i></p>
        `,
      },
      {
        title: "3. Placement",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Before</b> a conjugated verb: <i>Lo veo. No te entiendo.</i></li>
            <li><b>Attached</b> to an infinitive or gerund: <i>Voy a verlo / Estoy viéndolo.</i></li>
            <li><b>Attached</b> to affirmative commands: <i>¡Dímelo!</i> — but <b>before</b> negative ones: <i>No me lo digas.</i></li>
          </ul>
        `,
      },
      {
        title: "4. Reflexive & prepositional pronouns",
        body: `
          <p class="text-sage-700 mb-2"><b>Reflexive</b> pronouns show the subject acts on itself — used with reflexive verbs: <i>Me levanto</i> (I get [myself] up), <i>se llama</i> (he calls himself = his name is).</p>
          <p class="text-sage-700 text-sm"><b>Prepositional</b> pronouns follow words like <i>a, de, para, sin, por</i>: <i>para <b>mí</b>, sin <b>ti</b>, cerca de <b>nosotros</b></i>. Note the special forms <i>conmigo</i> and <i>contigo</i>.</p>
        `,
      },
    ],
    "",
    chart
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
